(ns frontend.view.modals.single-reference-relationship.view
  (:require
   [common.entity :as entity]
   [common.localization :as loc]
   [common.schema.field :as field]
   [common.schema.registry :as registry]
   [frontend.actions.entity :as entity-actions]
   [frontend.logging :as logging]
   [frontend.state.current-session :as session]
   [frontend.view.entity :as f-entity]
   [frontend.view.modals.single-reference-relationship.events :as events]
   [ui.components.alert.view :as alert]
   [ui.components.button.view :as button]
   [ui.components.modal.view :as modal]
   [ui.editors.labelled-input.view :as labelled-input]
   [ui.util :as uu]))

(defonce modal-key :single-reference-relationship-modal)

(defn resolve-accepted-entity-types
  [field-schema forced-entity-types]
  (let [stored-entity-types (set (:field/entity-types field-schema))
        select-entity-types (field/select-entity-types field-schema)]
    (cond
      (and (= stored-entity-types #{:entity.type/asset})
           forced-entity-types)
      forced-entity-types

      ;; Some legacy callers still force stored ref types. When a field stores
      ;; investor refs but selects person/company, keep the selector-side types.
      (and (seq forced-entity-types)
           (= (set forced-entity-types) stored-entity-types)
           (not= (set select-entity-types) stored-entity-types))
      select-entity-types

      (seq forced-entity-types)
      forced-entity-types

      :else
      select-entity-types)))

(defn render-target-type-selection
  [state entity-types type-label]
  (let [entity-type-options (->> entity-types
                                 (map (fn [entity-type]
                                        {:value entity-type
                                         :label (loc/tr entity-type)})))]
    [labelled-input/view
     {:editor-type   :editor.type/select
      :data-type     :data.type/keyword
      :label         type-label
      :is-clearable? false
      :value         (:selected-entity-type @state)
      :options       entity-type-options
      :on-change     (fn [new-value & _]
                       (when (not= new-value (:selected-entity-type @state))
                         (swap! state dissoc :target-id)
                         (swap! state assoc :selected-entity-type new-value)))}]))

(defn view
  [{:keys [props state]}]
  (let [tenant-schema @session/tenant-schema
        {:keys [forced-entity-types
                relationship-attribute
                reverse-relationship?]}
        props
        field-schema (registry/get-field-schema tenant-schema relationship-attribute)
        accepted-entity-types (resolve-accepted-entity-types field-schema forced-entity-types)
        selected-entity-type (if reverse-relationship?
                               (:containing-entity-type field-schema)
                               (if (= (count accepted-entity-types) 1)
                                 (first accepted-entity-types)
                                 (:selected-entity-type @state)))]
    (swap! state assoc :selected-entity-type selected-entity-type)
    (fn [{:keys [shown? close-modal! props state _class-name topmost?]}]
      (let [tenant-schema @session/tenant-schema
            {:keys [source-entity-id
                    title
                    forced-entity-types
                    pipeline-id
                    relationship-attribute
                    reverse-relationship?
                    type-label]}
            props
            on-close! close-modal!
            field-schema (registry/get-field-schema tenant-schema relationship-attribute)
            accepted-entity-types (resolve-accepted-entity-types field-schema forced-entity-types)
            selected-entity-type (if reverse-relationship?
                                   (:containing-entity-type field-schema)
                                   (if (= (count accepted-entity-types) 1)
                                     (first accepted-entity-types)
                                     (:selected-entity-type @state)))
            create-entity-types (field/create-entity-types field-schema)
            selected-create-entity-types (cond (empty? create-entity-types)
                                               [selected-entity-type]
                                               (= #{:entity.type/asset} (set create-entity-types))
                                               [selected-entity-type]
                                               (some #{selected-entity-type} create-entity-types)
                                               [selected-entity-type]
                                               :else
                                               (vec create-entity-types))]

        [modal/view
         {:visible?      (and shown? topmost?)
          :hide!         close-modal!
          :backdrop      "static"
          :close-button? true}
         (or title
             (if selected-entity-type
               (loc/tr :new-single-reference-relationship-title [(loc/tr selected-entity-type)])
               (loc/tr :new-single-reference-relationship-select-type)))
         [:<>
          #_[:div
             (pr-str selected-create-entity-types) " > " (pr-str selected-entity-type) " - " (pr-str forced-entity-types)]
          (when-let [error (:error @state)]
            [alert/danger
             {:on-close (fn [_]
                          (swap! state dissoc :error))}
             error])
          (if-not selected-entity-type
            [render-target-type-selection state accepted-entity-types type-label]
            ;; For :investor/legal-entity, hide persons/companies already claimed
            ;; by another investor. Backend enforces uniqueness in
            ;; backend.domain.investor/validate-field-update; this just keeps the
            ;; dropdown from offering options the save would reject. The current
            ;; investor's own legal-entity stays visible so the editor can render
            ;; its existing selection.
            (let [excluded-ids (when (and (= relationship-attribute :investor/legal-entity)
                                          (contains? #{:entity.type/person :entity.type/company}
                                                     selected-entity-type))
                                 (disj @session/claimed-legal-entity-ids
                                       (some-> source-entity-id
                                               session/get-entity
                                               :investor/legal-entity)))]
              [labelled-input/view
               {:editor-type               :editor.type/search
                :data-type                 :data.type/ref-single
                :label                     (loc/tr :select-entity [(loc/tr selected-entity-type)])
                :mandatory?                true
                :field/entity-types        [selected-entity-type]
                :field/select-entity-types [selected-entity-type]
                :field/create-entity-types selected-create-entity-types
                :exclude-values            (seq excluded-ids)
                :value                     (:value-opt @state)
                :on-change                 (fn [_value option]
                                             (swap! state assoc
                                                    :target-id (:value option)
                                                    :value-opt option))
                :create!                   (fn [input-str]
                                             (f-entity/open-creator!
                                              selected-create-entity-types
                                              {:start-value          input-str
                                               :creation-pipeline-id pipeline-id
                                               :skip-success-msg?    true
                                               :on-ok!               (fn [{:keys [new-entity-id new-entity close-cb!]}]
                                                                       (let [opt {:value new-entity-id
                                                                                  :label (entity/display-string new-entity)}]
                                                                         (swap! state assoc
                                                                                :target-id new-entity-id
                                                                                :value-opt opt))
                                                                       (when (fn? close-cb!) (close-cb!)))}))}]))]
         [:<>
          [modal/left-cancel-only on-close! nil]
          [:div
           {:class "flex gap-3 items-center justify-end"}
           (when (:single-reference-relationship-type @state)
             [button/secondary-hollow
              {:on-click (fn [_]
                           (swap! state dissoc :single-reference-relationship-type :target-id :new-option)
                           (deref state))}
              (loc/tr :back)])
           [button/primary-solid
            {:disabled? (not (events/form-ready? state))
             :on-click  (fn [_e]
                          (let [source-id (if reverse-relationship?
                                            (:target-id @state)
                                            source-entity-id)
                                target-id (if reverse-relationship?
                                            source-entity-id
                                            (:target-id @state))]
                            (entity-actions/*->field-update!
                             source-id
                             relationship-attribute
                             target-id
                             (fn [_]
                               (close-modal!))
                             (fn [err]
                               (logging/error "Failed to set single reference attribute" err)
                               (swap! state assoc :error (uu/error-response->message err))))))}
            (loc/tr :save)]]]]))))
